import React from "react";
import styles from "./DisplayDNA.module.css";
import FeatureLanes from "./FeatureLanes.js";
import OrfLanes from "./OrfLanes.js"; // NEW
import useMonospaceAdvance from "../hooks/useMonospaceAdvance.js";
import useDragSelectAutoscroll from "../hooks/useDragSelectAutoscroll.js";

export default function DisplayDNA({
  fwStrand = "",
  rwStrand = "",
  lineLength = 60,
  showLeftLabels = true,
  showRightLabels = true,
  onSelect,
  selectedRange,
  onEdit,
  styleVars,
  targetTextWidthPx,
  features = [],
  orfs = [], // NEW
}) {
  const wrapperRef = React.useRef(null);

  const totalLen = fwStrand.length;
  const numRows = Math.ceil(totalLen / Math.max(1, lineLength));

  const rows = React.useMemo(() => {
    const out = [];
    for (let i = 0; i < numRows; i++) {
      const start = i * lineLength;
      const end = Math.min(start + lineLength, totalLen);
      out.push({
        rowIndex: i,
        start,
        end,
        textTop: fwStrand.slice(start, end),
        textBottom: rwStrand.slice(start, end),
      });
    }
    return out;
  }, [fwStrand, rwStrand, lineLength, numRows, totalLen]);

  const { letterSpacingPx, charAdvancePx } = useMonospaceAdvance({
    wrapperRef,
    sampleText: rows[0]?.textTop || "A",
    lineSelectorClass: styles.fwLine,
    charactersPerLine: Math.max(1, lineLength),
    targetTextWidthPx: Math.max(1, targetTextWidthPx || 1),
  });

  const {
    handleMouseDownOnRow,
    handleMouseMoveOnRow,
    handleMouseUpGlobal,
    isDragging,
    justEndedDragRef,
  } = useDragSelectAutoscroll({
    wrapperRef,
    rows,
    charAdvancePx,
    onSelect,
  });

  function selectionIntersectionWithRow(row) {
    if (!selectedRange) return null;
    const selStart = Math.min(selectedRange.start, selectedRange.end);
    const selEnd = Math.max(selectedRange.start, selectedRange.end);
    const rowStart = row.start;
    const rowEndIncl = row.end - 1;
    if (selEnd < rowStart || selStart > rowEndIncl) return null;
    const s = Math.max(selStart, rowStart) - rowStart;
    const e = Math.min(selEnd, rowEndIncl) - rowStart;
    return { s, e, count: e - s + 1 };
  }

  function handleKeyDown(e) {
    if (!selectedRange) return;
    if (e.key === "Backspace" || e.key === "Delete") {
      onEdit && onEdit("delete");
      e.preventDefault();
    }
  }
  function handleCopy(e) {
    if (!selectedRange || !fwStrand) return;
    let { start, end } = selectedRange;
    if (start > end) [start, end] = [end, start];
    const seqToCopy = fwStrand.slice(start, end + 1);
    e.clipboardData.setData("text/plain", seqToCopy);
    e.preventDefault();
  }
  function handlePaste(e) {
    if (!selectedRange) return;
    const text = e.clipboardData.getData("text");
    if (text) {
      onEdit && onEdit("paste", text);
      e.preventDefault();
    }
  }

  // Auto-scroll selection into view (unless dragging / just after drag)
  React.useEffect(() => {
    if (!selectedRange || !wrapperRef.current || lineLength <= 0) return;
    if (isDragging) return;
    if (justEndedDragRef.current) return;

    const selStart = Math.min(selectedRange.start, selectedRange.end);
    if (!Number.isFinite(selStart)) return;

    const rowIndex = Math.floor(selStart / lineLength);
    const rowEl = wrapperRef.current.querySelector(
      `[data-row-index="${rowIndex}"]`
    );
    if (rowEl) {
      rowEl.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [selectedRange, lineLength, isDragging, justEndedDragRef]);

  return (
    <div
      className={styles.dnaWrapper}
      style={styleVars}
      ref={wrapperRef}
      onMouseUp={handleMouseUpGlobal}
    >
      {rows.map((row) => {
        const startLabel = row.start + 1;
        const endLabel = row.end;

        const selection = selectionIntersectionWithRow(row);
        const leftPx = selection ? selection.s * charAdvancePx : 0;
        const widthPx = selection ? selection.count * charAdvancePx : 0;
        const leftPxR = Math.round(leftPx);
        const widthPxR = Math.max(1, Math.round(widthPx));

        const lsStyle =
          letterSpacingPx !== null
            ? { letterSpacing: `${letterSpacingPx}px` }
            : undefined;

        return (
          <div
            className={styles.strandRow}
            key={row.rowIndex}
            data-row-index={row.rowIndex}
          >
            {showLeftLabels && (
              <span className={`${styles.strandLabel} ${styles.leftLabel}`}>
                {startLabel}
              </span>
            )}

            <div
              className={styles.lineBox}
              tabIndex={0}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onCopy={handleCopy}
              onMouseDown={(e) => handleMouseDownOnRow(e, row)}
              onMouseMove={(e) => handleMouseMoveOnRow(e, row)}
            >
              {selection && (
                <div
                  className={styles.selection}
                  style={{
                    left: `calc(var(--sp-pad, 8px) + ${leftPxR}px)`,
                    width: `${widthPxR}px`,
                  }}
                />
              )}

              <div
                className={`${styles.textLine} ${styles.fwLine}`}
                aria-label="Top strand"
                style={lsStyle}
              >
                {row.textTop}
              </div>

              <div
                className={`${styles.textLine} ${styles.rwLine}`}
                aria-label="Bottom strand"
                style={lsStyle}
              >
                {row.textBottom}
              </div>

              {/* NEW: ORFs directly under the sequence lines */}
              <OrfLanes
                row={row}
                orfs={orfs}
                charAdvancePx={charAdvancePx}
                onSelectRange={onSelect}
                topSequence={fwStrand}
              />

              {/* Features are shown below ORFs */}
              <FeatureLanes
                row={row}
                features={features}
                charAdvancePx={charAdvancePx}
                onSelectRange={onSelect}
              />
            </div>

            {showRightLabels && (
              <span className={`${styles.strandLabel} ${styles.rightLabel}`}>
                {endLabel}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
